<template>
  <div class="operating-country-table">
    <UniversalTable
      :config="tableConfigWithFilters"
      :items="paginatedCountries"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @onSearch="handleSearch"
      @onSort="handleSort"
      @removeFilter="removeFilter"
      @removeAllFilters="removeAllFilters"
      @onActionClick="handleAddCountry"
      backend-pagination
      @onRequest="handleRequest"
      :externalCurrentPage="currentPage"
      :items-count="filteredCount"
      :cellContainerMaxWidth="tableConfig.tableParams.value.cellContainerMaxWidth"
      :emptyObjectData="{ singular: 'Country', plural: 'Countries' }"
    />
    <OperatingCountriesTableFilterModal
      v-if="newFilterModalEnabled"
      :value="viewState.state.filters.showFilterDialog"
      :status-filter="viewState.state.filters.values"
      @input="viewState.state.filters.showFilterDialog = $event"
      @apply="handleApplyFilters"
      @close="viewState.state.filters.showFilterDialog = false"
    />
    <SearchModal
      v-else
      :dialog="viewState.state.filters.showFilterDialog"
      :statusOptions="statusOptions"
      :filterGroups="filterGroups"
      :defaultSelected="viewState.state.filters.values"
      :clearSelection="clearSelection"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="onFilterAssignments"
    />
    <AddOperatingCountryDialog
      :value="showAddDialog"
      :countries="disabledCountries"
      :loading="false"
      :error-code="addErrorCode"
      @input="showAddDialog = $event"
      @close="showAddDialog = false"
      @onAdd="onAddCountryConfirm"
    />
    <ExActionsPopUp
      v-if="showRetirePopup"
      headline="Retire Country"
      :htmlInfo="retireHtmlInfo"
      actionLabel="Retire"
      :value="showRetirePopup"
      :termination="true"
      :deletePopup="true"
      icon="country_icon"
      @onAction="confirmRetire"
      @input="cancelRetire"
    />
    <ExActionsPopUp
      v-if="showReactivatePopup"
      headline="Re-Activate Country"
      :htmlInfo="reactivateHtmlInfo"
      actionLabel="Re-Activate"
      :value="showReactivatePopup"
      :termination="false"
      :deletePopup="false"
      :applyBorderClass="true"
      icon="country_icon"
      @onAction="confirmReactivate"
      @input="cancelReactivate"
    />
    <ChecklistDrawer
      :isCountryChatter="true"
      ref="drawerContainer"
      :drawer="drawerOpen"
      :listCriteria="countryCriteria"
      @closeDrawer="closeChatter"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalCheckpointChatter"
      @requestMoreCheckpointsChatter="$emit('requestMoreChatter', $event)"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import OperatingCountriesTableFilterModal from "@components/OperatingCountriesTableFilterModal/OperatingCountriesTableFilterModal.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import AddOperatingCountryDialog from "@components/AddOperatingCountryDialog/AddOperatingCountryDialog.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import ChecklistDrawer from "@components/ChecklistCriteriaTable/ChecklistDrawer.vue";
import { LD_FLAG_IDEA_MDP_506 } from "@components/constants/launchDarklyFlagKeys";
import { useTableConfig } from "./composables/useTableConfig";
import { useOperatingCountryTableViewState } from "./composables/useOperatingCountryTableViewState";

const props = defineProps({
  // Preferred prop name (parity with currencies). Legacy `dataSource` still accepted.
  countries: {
    type: Array,
    default: null,
    },
    dataSource: {
      type: Array,
      default: () => [],
    },
  totalCount: {
    type: Number,
    default: 0,
  },
  externalTotal: {
      type: Number,
    default: null,
    },
    dropdownCountryList: {
      type: Array,
    default: () => [],
      },
  operatingCatalogCountryIds: {
    type: Array,
    default: () => [],
    },
  /** When true, status/search/page are owned by the parent (Settings path). */
    serverSidePagination: {
      type: Boolean,
      default: false,
    },
  syncedSearchQuery: {
    type: String,
    default: "",
  },
  chatterList: {
      type: Array,
      default: () => [],
    },
  totalCheckpointChatter: {
      type: Number,
    default: 0,
    },
  addErrorCode: {
      type: String,
      default: "",
    },
});

const emit = defineEmits([
  "fetch-countries",
  "search-countries",
  "sort-countries",
  "filter-countries",
  "add-country",
  "add-country-confirm",
  "onAddCountry",
  "set-work-hours",
  "menu-action",
  "chatter-click",
  "get-chatter-list",
  "requestMoreChatter",
  "country-page-change",
  "country-search",
  "country-status-filter",
  "ensure-country-catalog",
  "reloadCountryList",
]);

const tableConfig = useTableConfig({
  onSetWorkHours: (payload) => emit("set-work-hours", payload),
  onMenuAction: (payload) => handleMenuAction(payload),
  onChatterClick: (payload) => openChatter(payload?.item),
});
const viewState = useOperatingCountryTableViewState();

const currentPage = ref(1);
const itemsPerPage = ref(10);
const showAddDialog = ref(false);
const showRetirePopup = ref(false);
const showReactivatePopup = ref(false);
const pendingMenuItem = ref(null);
const drawerOpen = ref(false);
const countryCriteria = ref("");
const statusOptions = ["active", "retired"];
const filterGroups = [];
const clearSelection = ref("");

// OperatingCountriesTableFilterModal (ER-10430) is gated behind idea-MDP-506 so
// it can be rolled back to the legacy SearchModal independently of the wider
// table decomposition (ER-10431) it ships inside. Defaults on when no live
// LaunchDarkly client is present (Storybook / unit tests) so the new modal
// still previews/renders for review.
const instance = getCurrentInstance();
const newFilterModalEnabled = ref(false);

onMounted(async () => {
  try {
    if (instance?.proxy && typeof instance.proxy.$watchFlag === "function") {
      await instance.proxy.$watchFlag(LD_FLAG_IDEA_MDP_506, false);
      newFilterModalEnabled.value = !!instance.proxy.flags[LD_FLAG_IDEA_MDP_506];
    } else {
      newFilterModalEnabled.value = true;
    }
  } catch (e) {
    newFilterModalEnabled.value = true;
  }
});

const sourceCountries = computed(() => {
  if (Array.isArray(props.countries)) return props.countries;
  return props.dataSource || [];
});

const rowName = (item) => item.country?.name || item.name || "";
const rowCurrencyCode = (item) =>
  item.country?.currency?.code || item.currency_code || "";
const rowStatus = (item) =>
  item.status || (item.active === false ? "retired" : "active");
const rowIsDefault = (item) => item.is_default === true;

watch(
  () => props.syncedSearchQuery,
  (val) => {
    if (!props.serverSidePagination) return;
        const next = val == null ? "" : String(val);
    if (next !== (viewState.state.filters.searchText || "")) {
      viewState.state.filters.searchText = next;
    }
  }
);

const tableConfigWithFilters = computed(() => ({
  ...tableConfig.tableParams.value,
  filters: {
    values: viewState.state.filters.values || [],
    chipLabels: viewState.state.filters.values || [],
    searchText: viewState.state.filters.searchText,
    searchGhostText: viewState.state.filters.searchGhostText,
  },
}));

const filteredCountries = computed(() => {
  let list = [...sourceCountries.value];

  // Status is server-filtered when paginating via the API.
  if (!props.serverSidePagination) {
    const statuses = viewState.state.filters.values || [];
    if (statuses.length > 0) {
      list = list.filter((item) => statuses.includes(rowStatus(item)));
    }

    const search = (viewState.state.filters.searchText || "").trim().toLowerCase();
    if (search) {
      list = list.filter((item) => {
        const name = rowName(item).toLowerCase();
        const code = rowCurrencyCode(item).toLowerCase();
        return name.includes(search) || code.includes(search);
      });
    }
  }

  // Rule 11: Default first, then A→Z country name.
  list.sort((a, b) => {
    const aDef = rowIsDefault(a);
    const bDef = rowIsDefault(b);
    if (aDef && !bDef) return -1;
    if (!aDef && bDef) return 1;
    return rowName(a).localeCompare(rowName(b), undefined, {
      sensitivity: "base",
    });
  });

  return list;
});

const filteredCount = computed(() => {
  if (props.serverSidePagination) {
    if (typeof props.externalTotal === "number") return props.externalTotal;
    if (typeof props.totalCount === "number" && props.totalCount > 0) {
      return props.totalCount;
    }
  }
  return filteredCountries.value.length;
});

const paginatedCountries = computed(() => {
  if (props.serverSidePagination) {
    return filteredCountries.value;
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredCountries.value.slice(start, start + itemsPerPage.value);
});

const disabledCountries = computed(() => {
  const existingIds = new Set(
    (Array.isArray(props.operatingCatalogCountryIds)
      ? props.operatingCatalogCountryIds
            : []
          )
            .filter((id) => id != null && id !== "")
            .map((id) => String(id))
        );

  // Client-only path: also grey out by id from the provided rows.
  if (!props.serverSidePagination) {
    sourceCountries.value.forEach((row) => {
      const id = row.country?.id;
      if (id != null && id !== "") existingIds.add(String(id));
    });
  }

  return (props.dropdownCountryList || []).map((country) => {
        if (!country || typeof country !== "object") {
          return { id: undefined, name: "", disabled: false };
        }
    const alreadyLinked =
      country.id != null &&
            country.id !== "" &&
      existingIds.has(String(country.id));
        return {
          ...country,
      disabled: !!alreadyLinked || country.already_linked === true,
        };
      });
});

const retireHtmlInfo = computed(() => {
  const name = rowName(pendingMenuItem.value) || "this country";
  return `You are about to retire <strong>${name}</strong>. Retired countries can be re-activated later.`;
});

const reactivateHtmlInfo = computed(() => {
  const name = rowName(pendingMenuItem.value) || "this country";
  return `You are about to re-activate <strong>${name}</strong>.`;
});

const handleSearch = (search) => {
  viewState.state.filters.searchText = search || "";
  currentPage.value = 1;
  emit("search-countries", { search });
  if (props.serverSidePagination) {
    emit("country-search", search || "");
  }
};

const handleSort = (sortData) => {
  emit("sort-countries", sortData);
};

const handleRequest = async ({ page, itemsPerPage: perPage }) => {
  currentPage.value = page;
  itemsPerPage.value = perPage || 10;
  emit("fetch-countries", { page, itemsPerPage: perPage || 10 });
  if (props.serverSidePagination) {
    emit("country-page-change", page);
  }
};

/** Shared apply core for both filter UIs — keeps emit contracts identical
 *  regardless of which modal (new or legacy) produced the selection. */
const applyStatusValues = (values) => {
  const next = (values || [])
    .map((v) => String(v).toLowerCase())
    .filter((v) => statusOptions.includes(v));

  viewState.state.filters.values = next;
  viewState.state.filters.showFilterDialog = false;
  currentPage.value = 1;

  emit("filter-countries", {
    status: viewState.state.filters.values,
    action: "apply",
  });
  if (props.serverSidePagination) {
    emit("country-status-filter", [...viewState.state.filters.values]);
  }
};

/** OperatingCountriesTableFilterModal emits apply(statusValues) as a plain string array. */
const handleApplyFilters = (statusValues) => applyStatusValues(statusValues);

/** Legacy SearchModal emits [{ label, value }, ...] for status checkboxes. */
const onFilterAssignments = (checkedItems) => {
  const next = (checkedItems || []).map((item) =>
    typeof item === "string" ? item : item?.value || item?.label || ""
  );
  applyStatusValues(next);
};

const handleResetFilters = () => {
  viewState.state.filters.values = ["active"];
  currentPage.value = 1;

  emit("filter-countries", {
    status: viewState.state.filters.values,
    action: "reset",
  });
  if (props.serverSidePagination) {
    emit("country-status-filter", [...viewState.state.filters.values]);
  }
};

const removeFilter = async (chip) => {
  const label = typeof chip === "object" ? chip.value ?? chip.label : chip;
  const status = typeof label === "string" ? label.toLowerCase() : "";
  if (statusOptions.includes(status)) {
    viewState.state.filters.values = viewState.state.filters.values.filter(
      (s) => s !== status
    );
    clearSelection.value = status;
  }
  currentPage.value = 1;

  emit("filter-countries", {
    status: viewState.state.filters.values,
    action: "remove",
  });
  if (props.serverSidePagination) {
    emit("country-status-filter", [...viewState.state.filters.values]);
  }
};

const removeAllFilters = async () => {
  viewState.state.filters.values = [];
  viewState.state.filters.searchText = "";
  clearSelection.value = "clear-all";
  currentPage.value = 1;

  emit("filter-countries", { action: "removeAll" });
  if (props.serverSidePagination) {
    emit("country-search", "");
    emit("country-status-filter", []);
  }
};

const handleAddCountry = () => {
  showAddDialog.value = true;
  emit("add-country");
  emit("ensure-country-catalog");
};

const onAddCountryConfirm = (country) => {
  const name =
    (typeof country?.name === "string" && country.name.trim()) ||
    (typeof country?.country?.name === "string" && country.country.name.trim()) ||
    "";
  // Auto-fill SearchBar with the added country so it is immediately visible
  // (parity with CurrencyTable.addCurrency).
  if (name) {
    viewState.state.filters.searchText = name;
    currentPage.value = 1;
    emit("country-search", name);
    emit("search-countries", { search: name });
  }
  emit("add-country-confirm", country, name);
  emit("onAddCountry", country, name);
  showAddDialog.value = false;
};

function handleMenuAction(payload) {
  const action = payload?.action;
  const item = payload?.item;
  if (!action || !item) return;

  if (action === "retire") {
    pendingMenuItem.value = item;
    showRetirePopup.value = true;
    return;
  }
  if (action === "reactivate") {
    pendingMenuItem.value = item;
    showReactivatePopup.value = true;
    return;
  }
  // setAsDefault — parent owns the API (no client PATCH loop).
  emit("menu-action", { item, action });
}

function confirmRetire() {
  if (pendingMenuItem.value) {
    emit("menu-action", { item: pendingMenuItem.value, action: "retire" });
  }
  showRetirePopup.value = false;
  pendingMenuItem.value = null;
}

function cancelRetire() {
  showRetirePopup.value = false;
  pendingMenuItem.value = null;
}

function confirmReactivate() {
  if (pendingMenuItem.value) {
    emit("menu-action", { item: pendingMenuItem.value, action: "reactivate" });
  }
  showReactivatePopup.value = false;
  pendingMenuItem.value = null;
}

function cancelReactivate() {
  showReactivatePopup.value = false;
  pendingMenuItem.value = null;
}

function openChatter(item) {
  if (!item) return;
  drawerOpen.value = true;
  countryCriteria.value = rowName(item);
  emit("chatter-click", { item });
  emit("get-chatter-list", item);
}

function closeChatter() {
  drawerOpen.value = false;
  countryCriteria.value = "";
}

defineExpose({
  filteredCountries,
  paginatedCountries,
  filteredCount,
  disabledCountries,
  showAddDialog,
  showRetirePopup,
  showReactivatePopup,
  viewState,
  handleAddCountry,
  onAddCountryConfirm,
  handleMenuAction,
  confirmRetire,
  cancelRetire,
  confirmReactivate,
  cancelReactivate,
  handleSearch,
  handleRequest,
  handleApplyFilters,
  handleResetFilters,
  onFilterAssignments,
  removeFilter,
  removeAllFilters,
  newFilterModalEnabled,
});
</script>

<style scoped>
.operating-country-table {
  width: 100%;
}
</style>

<!--
  Column alignment for the 6-column layout (Country · Currency · Work hours ·
  Status · Chatter · Menu). Global (unscoped) because UniversalTable renders
  the table rows; namespaced under .operating-country-table.
-->
<style>
.operating-country-table .ex-universal-table-container {
  box-shadow: 0px 1px 2px 0px #1018280f, 0px 1px 3px 0px #1018281a !important;
  border-radius: 8px;
}

.operating-country-table tbody tr td {
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

/* Center Currency (2), Status (4), Chatter (5), Menu (6). */
.operating-country-table thead tr th:nth-child(2),
.operating-country-table thead tr th:nth-child(4),
.operating-country-table thead tr th:nth-child(5),
.operating-country-table thead tr th:nth-child(6),
.operating-country-table tbody tr td:nth-child(2),
.operating-country-table tbody tr td:nth-child(4),
.operating-country-table tbody tr td:nth-child(5),
.operating-country-table tbody tr td:nth-child(6) {
  text-align: center !important;
}

.operating-country-table thead tr th:nth-child(2) > div,
.operating-country-table thead tr th:nth-child(4) > div,
.operating-country-table thead tr th:nth-child(5) > div,
.operating-country-table thead tr th:nth-child(6) > div {
  justify-content: center !important;
}

.operating-country-table tbody tr td:nth-child(1) .cell-container {
  max-width: none;
}

.operating-country-table tbody tr td:nth-child(2) .cell-container,
.operating-country-table tbody tr td:nth-child(4) .cell-container,
.operating-country-table tbody tr td:nth-child(5) .cell-container,
.operating-country-table tbody tr td:nth-child(6) .cell-container {
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: none !important;
  overflow: visible;
}

.operating-country-table tbody tr td:nth-child(6) {
  overflow: visible;
  padding-right: 8px !important;
}

.operating-country-table .ex-menu-icon {
  margin: 0px 5px 5px !important;
}

/* Toolbar (search + Add Country): inset from the card edge (Storybook + Settings). */
.operating-country-table .ex-universal-table-container .table-content {
  padding-right: 16px !important;
  box-sizing: border-box;
}

.operating-country-table .table-content > .mb-5.d-flex.align-start {
  padding-right: 8px;
  box-sizing: border-box;
}

.operating-country-table .table-content > .mb-5.d-flex.align-start .mt-3 {
  margin-right: 8px;
}
</style>
